import { Fragment } from "react";
import React from "react";
import { useMutation } from "@apollo/react-hooks";
import ScrollTable, {
  ScrollTableBody,
  ScrollTableBodyCell,
  ScrollTableBodyRow,
  ScrollTableHeader,
  ScrollTableHeaderCell,
  ScrollTableNoContent,
  ScrollTableSpace,
} from "./ScrollableTable";
import { PaginationTypes } from "@sellout/models/.dist/interfaces/IPagination";
import DELETE_HOLD_TICKET from "@sellout/models/.dist/graphql/mutations/deleteTicketHold.mutation"; 
import { NoContentHead } from "./OrdersTable";
import { useDispatch, useSelector } from "react-redux";
import { BackstageState } from "../redux/store";
import * as AppActions from "../redux/actions/app.actions";
import { ModalTypes } from "./modal/Modal";
import ITicketHold from "@sellout/models/.dist/interfaces/ITicketHold";
import * as EventActions from "../redux/actions/event.actions";
import { Icon, Icons, Colors } from "@sellout/ui";
import { AppNotificationTypeEnum } from "../models/interfaces/IAppNotification";
import styled from "styled-components";


const IconWrapper = styled.div<{ canDelete: boolean }>`
  cursor: ${props => props.canDelete ? 'pointer' : 'not-allowed'};
  display: inline-block;
`;

export enum TicketHoldsTableTextEnum {
  NoTicketHolds = "No ticket holds",
  BlockName = "Block Name",
  TicketType = "Ticket Type",
  TotalHeld = "Total Held",
  TotalCheckedIn = "Total Checked In",
  TotalReleased = "Total Released",
  TotalOutstanding = "Total Outstanding",
  Actions = "Actions",
  DeleteHoldTitle = "Delete Ticket Hold",
  DeleteHoldMessage = "Are you sure you want to delete the hold \{name}\?",
  DeleteHoldConfirm = "DELETE",
  DeleteHoldCancel = "CANCEL",
  DeleteHoldCheckedInError = "Cannot delete hold: tickets have been checked in",
  DeleteHoldReleasedError = "Cannot delete hold: tickets have been released",
  DeleteHoldNotImplemented = "Delete hold functionality not yet implemented",
}

type TableProps = {};
const TableHeader: React.FC<TableProps> = ({}) => {
  return (
    <ScrollTableHeader>
      <ScrollTableHeaderCell width="200px">{TicketHoldsTableTextEnum.BlockName}</ScrollTableHeaderCell>
      <ScrollTableSpace />
      <ScrollTableHeaderCell width="200px">{TicketHoldsTableTextEnum.TicketType}</ScrollTableHeaderCell>
      <ScrollTableSpace />
      <ScrollTableHeaderCell width="200px">{TicketHoldsTableTextEnum.TotalHeld}</ScrollTableHeaderCell>
      <ScrollTableSpace />
      <ScrollTableHeaderCell width="200px">{TicketHoldsTableTextEnum.TotalCheckedIn}</ScrollTableHeaderCell>
      <ScrollTableSpace />
      <ScrollTableHeaderCell width="200px">{TicketHoldsTableTextEnum.TotalReleased}</ScrollTableHeaderCell>
      <ScrollTableSpace />
      <ScrollTableHeaderCell width="200px">{TicketHoldsTableTextEnum.TotalOutstanding}</ScrollTableHeaderCell>
      <ScrollTableSpace />
      <ScrollTableHeaderCell width="100px">{TicketHoldsTableTextEnum.Actions}</ScrollTableHeaderCell>
      <ScrollTableSpace />
    </ScrollTableHeader>
  );
};


type TableRowsProps = {
  holdTickets: ITicketHold[];
  refetch?: Function;
} & TableProps;

const TableRows: React.FC<TableRowsProps> = ({ holdTickets, refetch }) => {
  const dispatch = useDispatch();
  const eventState = useSelector((state: BackstageState) => state.event);
  const { eventId } = eventState;
  
  const [deleteHoldTicket] = useMutation(DELETE_HOLD_TICKET);

  const handleDelete = (holdTicket: any, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (holdTicket.totalCheckedIn > 0) {
      dispatch(
        AppActions.showNotification(
          TicketHoldsTableTextEnum.DeleteHoldCheckedInError,
          AppNotificationTypeEnum.Error
        )
      );
      return;
    }
    if (holdTicket.totalReleased > 0) {
      dispatch(
        AppActions.showNotification(
          TicketHoldsTableTextEnum.DeleteHoldReleasedError,
          AppNotificationTypeEnum.Error
        )
      );
      return;
    }
    dispatch(
      AppActions.pushModalConfirmAction({
        title: TicketHoldsTableTextEnum.DeleteHoldTitle,
        message: TicketHoldsTableTextEnum.DeleteHoldMessage
          .replace("{name}", holdTicket.name)
          .replace("{count}", holdTicket.totalOutstanding),
        confirm: async () => {
          try {
            await deleteHoldTicket({ 
              variables: { 
                eventId,
                holdId: holdTicket._id 
              } 
            });
            
            if (refetch) {
              await refetch();
            }
            
            dispatch(
              AppActions.showNotification(
                "Ticket hold deleted successfully",
                AppNotificationTypeEnum.Success
              )
            );
          } catch (error) {
            dispatch(
              AppActions.showNotification(
                "Failed to delete ticket hold",
                AppNotificationTypeEnum.Error
              )
            );
          }
          dispatch(AppActions.popModal());
        },
        confirmText: TicketHoldsTableTextEnum.DeleteHoldConfirm,
        cancel: () => {
          dispatch(AppActions.popModal());
        },
        cancelText: TicketHoldsTableTextEnum.DeleteHoldCancel,
      })
    );
  };

  return (
    <>
      {holdTickets?.map((holdTicket: any) => {
        const onClick = () => {
          dispatch(EventActions.setTicketHoldId(holdTicket?._id));
          dispatch(AppActions.pushModal(ModalTypes.UpdateTicketBlockModal));
        };
        
        const canDelete = holdTicket?.totalCheckedIn === 0 && holdTicket?.totalReleased === 0;
        return (
          <ScrollTableBodyRow
            key={holdTicket._id}
            height="40px"
            onClick={(e) => onClick()}
          >
            <ScrollTableBodyCell width="200px">
            {holdTicket.name}
            </ScrollTableBodyCell>
            <ScrollTableSpace />
            <ScrollTableBodyCell width="200px">
              {holdTicket?.ticketType}
            </ScrollTableBodyCell>
            <ScrollTableSpace />
            <ScrollTableBodyCell width="200px">
              {holdTicket.totalHeld}
            </ScrollTableBodyCell>
            <ScrollTableSpace />

            <ScrollTableBodyCell width="200px">
              {holdTicket.totalCheckedIn}
            </ScrollTableBodyCell>
            <ScrollTableSpace />

            <ScrollTableBodyCell width="200px">
              {holdTicket.totalReleased}
            </ScrollTableBodyCell>
            <ScrollTableSpace />
            <ScrollTableBodyCell width="200px">
              {holdTicket.totalOutstanding}
            </ScrollTableBodyCell>
            <ScrollTableSpace />
            <ScrollTableBodyCell width="100px">
              <IconWrapper canDelete={canDelete}>
                <Icon
                  icon={Icons.DeleteRegular}
                  color={canDelete ? Colors.Red : Colors.Grey3}
                  size={16}
                  onClick={canDelete ? (e) => handleDelete(holdTicket, e) : (e) => {
                    e.stopPropagation();
                    handleDelete(holdTicket, e);
                  }}
                />
              </IconWrapper>
            </ScrollTableBodyCell>
            <ScrollTableSpace />
          </ScrollTableBodyRow>
        );
      })}
    </>
  );
};

type OrdersTableProps = {
  holdTickets: any;
  fetchMore?: Function;
  refetch?: Function;
  paginationType?: PaginationTypes;
  batchPrint?: boolean;
} & TableProps;

const TicketHoldsTable: React.FC<OrdersTableProps> = ({
  holdTickets,
  fetchMore,
  paginationType,
  refetch
}) => {
  const scrollContainer = React.useRef<any>(null);

  /** Render */
  if (holdTickets?.length <= 0) {
    return (
      <ScrollTable>
        <TableHeader />
        <ScrollTableBody>
          <ScrollTableNoContent>
            <NoContentHead>{TicketHoldsTableTextEnum.NoTicketHolds}</NoContentHead>
          </ScrollTableNoContent>
        </ScrollTableBody>
      </ScrollTable>
    );
  }

  return (
    <ScrollTable
      fetchMore={fetchMore}
      paginationType={paginationType}
      scrollContainer={scrollContainer}
      name={"holdTickets"}
    >
      {(loading: boolean) => {
        return (
          <Fragment>
            <TableHeader />
            <ScrollTableBody ref={scrollContainer}>
              <TableRows holdTickets={holdTickets} refetch={refetch} />
            </ScrollTableBody>
          </Fragment>
        );
      }}
    </ScrollTable>
  );
};

export default TicketHoldsTable;
